const SCREEN_AUTH_KEY = 'screenAuth';

const parseArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (error) {
      // Fall back to comma-separated values
      const parts = value.split(',').map((item) => item.trim()).filter(Boolean);
      if (parts.length) {
        return parts;
      }
    }
  }

  return [];
};

const normalizeAuthData = (data) => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const expiry = data.expiry_date ? new Date(data.expiry_date).toISOString() : undefined;

  return {
    token: data.token,
    screen_code: data.screen_code,
    views: parseArray(data.views),
    order_status: parseArray(data.order_status),
    store_ids: parseArray(data.store_ids),
    expiry_date: expiry,
  };
};

export const persistScreenAuthData = (data = {}) => {
  const normalized = normalizeAuthData(data);
  if (!normalized?.token) {
    return;
  }

  localStorage.setItem(SCREEN_AUTH_KEY, JSON.stringify(normalized));
  sessionStorage.setItem('screenToken', normalized.token);
  if (normalized.screen_code) {
    sessionStorage.setItem('screenCode', normalized.screen_code);
  }
  sessionStorage.setItem('screenViews', JSON.stringify(normalized.views || []));
  sessionStorage.setItem('screenOrderStatus', JSON.stringify(normalized.order_status || []));
  sessionStorage.setItem('screenStoreIds', JSON.stringify(normalized.store_ids || []));
  if (normalized.expiry_date) {
    sessionStorage.setItem('screenExpiry', normalized.expiry_date);
  }
};

export const getScreenAuthData = () => {
  const raw = localStorage.getItem(SCREEN_AUTH_KEY);
  if (!raw) {
    return null;
  }

  try {
    return normalizeAuthData(JSON.parse(raw));
  } catch (error) {
    console.warn('Unable to parse screen auth data', error);
    localStorage.removeItem(SCREEN_AUTH_KEY);
    return null;
  }
};

// Username storage utilities with fallback support
export const persistScreenUsername = (username) => {
  if (!username) return;
  
  try {
    sessionStorage.setItem('screenUsername', username);
    // Also store in localStorage as backup
    localStorage.setItem('screenUsername', username);
  } catch (e) {
    // If sessionStorage fails, try localStorage only
    try {
      localStorage.setItem('screenUsername', username);
    } catch (err) {
      console.error('Failed to store username:', err);
    }
  }
};

export const getScreenUsername = () => {
  try {
    // Try sessionStorage first
    const sessionUsername = sessionStorage.getItem('screenUsername');
    if (sessionUsername) return sessionUsername;
    
    // Fallback to localStorage
    const localUsername = localStorage.getItem('screenUsername');
    if (localUsername) {
      // Sync back to sessionStorage if found in localStorage
      try {
        sessionStorage.setItem('screenUsername', localUsername);
      } catch (e) {}
      return localUsername;
    }
  } catch (e) {
    // If sessionStorage is blocked, try localStorage only
    try {
      return localStorage.getItem('screenUsername') || '';
    } catch (err) {
      return '';
    }
  }
  return '';
};

export const clearScreenUsername = () => {
  try {
    sessionStorage.removeItem('screenUsername');
  } catch (e) {
    console.error('Failed to clear sessionStorage username:', e);
  }
  try {
    localStorage.removeItem('screenUsername');
  } catch (e) {
    console.error('Failed to clear localStorage username:', e);
  }
};

export const clearScreenAuthData = () => {
  localStorage.removeItem(SCREEN_AUTH_KEY);
  sessionStorage.removeItem('screenToken');
  sessionStorage.removeItem('screenCode');
  sessionStorage.removeItem('screenViews');
  sessionStorage.removeItem('screenOrderStatus');
  sessionStorage.removeItem('screenStoreIds');
  sessionStorage.removeItem('screenExpiry');
  // Also clear username
  clearScreenUsername();
};

export const getSessionScreenAuth = () => {
  const token = sessionStorage.getItem('screenToken');
  if (!token) {
    return null;
  }

  return {
    token,
    screen_code: sessionStorage.getItem('screenCode') || undefined,
    views: parseArray(sessionStorage.getItem('screenViews')),
    order_status: parseArray(sessionStorage.getItem('screenOrderStatus')),
    store_ids: parseArray(sessionStorage.getItem('screenStoreIds')),
    expiry_date: sessionStorage.getItem('screenExpiry') || undefined,
  };
};

export const syncScreenSessionFromCache = () => {
  const cached = getScreenAuthData();
  if (cached?.token) {
    persistScreenAuthData(cached);
  }
};

export const isScreenSessionExpired = (expiryDate) => {
  if (!expiryDate) {
    return false;
  }

  return new Date(expiryDate) < new Date();
};
